import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Redirect, Route, Switch } from 'react-router'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import withApiError, {
  ERROR_TYPE_LMS,
  ERROR_TYPE_PROFILE,
  SHOW_ERROR_IN_MODAL,
} from 'layers/errorHandling/apiError'
import { productMap } from 'utils/shapes'
import { operations as productOperations } from 'store/productManager'
import { Loading, ErrorMessage, NotFound } from 'secondstep-components'
import PagesContainer from 'containers/Pages'
import PagesV2Container from 'containers/PagesV2'
import Sela from 'pages/Sela'
import Training from 'pages/Training'

import LessonsCurriculum from 'pages/LessonsCurriculum'
import K8Teach from 'pages/K8Teach'
import TieredInterventionsUnits from 'pages/TieredInterventionsUnits'
import { UnitV3Page as UnitV3 } from 'pages/UnitV3'

import IndividualResource from 'pages/IndividualResourceV2'
import IndividualResourceV3 from 'pages/IndividualResourceV3'
import ResourcesSubLandingPageV3 from 'pages/ResourcesSubLandingPageV3'
import ResourcesContentV3 from 'pages/ResourcesContentV3'
import { TrainingArticleV3 } from 'pages/ResourcesContentV3/training'
import TrainingLandingV3 from 'pages/TrainingLandingV3'
import ResourcesLanding from 'pages/ResourcesLanding'
import CurriculumRedirectRule from 'pages/CurriculumRedirectRule'
// TODO: fix import when the High School + Middle School flags are removed:
import ResourceContent from 'pages/ResourcesContent/flag.on'
import LearnErrorBoundary from 'components/LearnErrorBoundary'

import withSiteContext from 'layers/content/v2/Hocs/withSiteContext'

import { getStaticRoutes, PAGES_V2_STATIC_ROUTES } from './data'
import { ProductWrapper } from './component.styles'
import { HIGH_SCHOOL_INDIVIDUAL_RESOURCE_PATH } from 'utils/highschoolHelpers'
import {
  ELEMENTARY_INDIVIDUAL_RESOURCE_PATH,
  MIDDLE_SCHOOL_INDIVIDUAL_RESOURCE_PATH,
} from 'utils/program/helpers'
import {
  DWB_PROGRAM_SUPPORTS_ARTICLE,
  DWB_PROGRAM_SUPPORTS_BASE_PATH,
} from 'utils/routing/routeHelpers'

import {
  HIGH_SCHOOL_LESSON_RESOURCE_PATH,
  INDIVIDUAL_RESOURCE_V3_ROUTES,
  K8_LESSON_RESOURCE_PATH,
  K8_PATH_TO_UNIT,
  LESSON_PATH_TO_LE,
  LESSONS_LANDING_ROUTES,
  MISSING_PRODUCT_ERROR,
  SELA_PRODUCT_NAMES,
  TRAINING_ARTICLE_ROUTES,
  TRAINING_LANDING_ROUTES,
  DWB_TRAINING_ARTICLE_ROUTES,
} from './constants'

// Digital Well-Being unit pages resolve at the grade segment.
const DWB_PATH_TO_UNIT =
  '/product/digital-well-being/lessons/:verb(preview|teach)/:grade'

export const ProductContainer = ({
  getProductMap,
  location,
  match,
  productMap,
}) => {
  const [productError, setProductError] = useState(null)
  const productName = match?.params?.productName
  const path = match?.path
  useEffect(() => {
    const map = getProductMap(productName)

    setProductError(map ? null : MISSING_PRODUCT_ERROR)
  }, [getProductMap, productName])

  const staticPageRoutes = useMemo(() => getStaticRoutes(path), [path])

  const isLessonsLanding = location?.pathname?.endsWith('/lessons')
  const isSelaPath = SELA_PRODUCT_NAMES.includes(productName)

  if (productError) {
    return (
      <ErrorMessage
        additionalDetails={productError.errorDetails}
        statusCode={productError.errorStatus}
      />
    )
  }

  if (!productMap) {
    return <Loading loadingText="Loading program data..." />
  }

  return (
    <LearnErrorBoundary>
      <ProductWrapper
        isLessonsLanding={isLessonsLanding}
        isSelaPath={isSelaPath}
      >
        <Switch>
          <Route component={UnitV3} exact path={DWB_PATH_TO_UNIT} />
          <Route component={UnitV3} exact path={K8_PATH_TO_UNIT} />
          <Route
            component={LessonsCurriculum}
            exact={true}
            path={LESSONS_LANDING_ROUTES}
          />
          {/* K-8 (elementary/middle-school) learningExperience pages all use IndividualResourceV3 */}
          <Route
            component={IndividualResourceV3}
            path={[
              ...INDIVIDUAL_RESOURCE_V3_ROUTES,
              LESSON_PATH_TO_LE,
              K8_LESSON_RESOURCE_PATH,
            ]}
          />
          {/* High school lessons-tree resources stay on IndividualResourceV2 */}
          <Route
            component={IndividualResource}
            path={HIGH_SCHOOL_LESSON_RESOURCE_PATH}
          />

          {/* DWB program supports — must stay above PagesContainer (staticPageRoutes) */}
          <Route
            component={ResourcesSubLandingPageV3}
            exact
            path={DWB_PROGRAM_SUPPORTS_BASE_PATH}
          />
          <Route
            component={ResourcesContentV3}
            path={DWB_PROGRAM_SUPPORTS_ARTICLE}
          />

          <Route
            component={TrainingLandingV3}
            exact
            path={TRAINING_LANDING_ROUTES}
          />
          <Route component={TrainingArticleV3} path={TRAINING_ARTICLE_ROUTES} />
          <Route
            component={TrainingArticleV3}
            path={DWB_TRAINING_ARTICLE_ROUTES}
          />

          <Route
            component={TieredInterventionsUnits}
            exact
            path="/product/:program/lessons/tier-2/:verb/:gradeBand/:focusArea"
          />

          <Route
            component={IndividualResourceV3}
            path={[
              `${ELEMENTARY_INDIVIDUAL_RESOURCE_PATH}/:slug`,
              `${MIDDLE_SCHOOL_INDIVIDUAL_RESOURCE_PATH}/:slug`,
            ]}
          />
          {/* High school learning-experiences LEs stay on IndividualResourceV2 */}
          <Route
            component={IndividualResource}
            path={`${HIGH_SCHOOL_INDIVIDUAL_RESOURCE_PATH}/:slug`}
          />

          <Route
            component={CurriculumRedirectRule}
            exact
            path="/product/high-school/pathways"
          />
          <Route
            component={ResourceContent}
            path="/product/high-school/pathways/:pathway_name/overview"
          />
          <Route
            component={ResourcesLanding}
            exact
            path="/product/high-school/program-supports"
          />
          {/* these routes will use PagesV2Container. order here matters so please make sure it's before 'PagesContainer' */}

          <Route
            component={PagesV2Container}
            path={PAGES_V2_STATIC_ROUTES.highSchool}
          />

          <Route
            component={PagesV2Container}
            path={PAGES_V2_STATIC_ROUTES.middleSchool}
          />

          <Route
            component={withSiteContext(PagesV2Container)}
            path={PAGES_V2_STATIC_ROUTES.elementary}
          />
          <Route component={PagesContainer} path={staticPageRoutes} />
          <Route component={Sela} path={`${path}/modules`} />

          <Route
            component={K8Teach}
            exact
            path={[
              '/product/middle-school/lessons/:verb/:course',
              '/product/elementary/lessons/:verb/:course',
            ]}
          />

          <Route component={Training} path={`${path}/training`} />
          <Redirect
            exact
            from={path}
            to={`${path}/${productMap.defaultPage}`}
          />
          <Route component={NotFound} />
        </Switch>
      </ProductWrapper>
    </LearnErrorBoundary>
  )
}

ProductContainer.propTypes = {
  getProductMap: PropTypes.func.isRequired,
  lmsError: PropTypes.object,
  location: PropTypes.object,
  match: PropTypes.object.isRequired,
  productMap,
  resetContentNodes: PropTypes.func,
  theme: PropTypes.object,
}

const selectProductMap = state => state?.productManager?.productMap

const ConnectedProductContainer = props => {
  const dispatch = useDispatch()
  const selectedProductMap = useSelector(selectProductMap, shallowEqual)
  const getProductMap = useCallback(
    productName => dispatch(productOperations.getProductMap(productName)),
    [dispatch],
  )

  return (
    <ProductContainer
      {...props}
      getProductMap={getProductMap}
      productMap={selectedProductMap}
    />
  )
}

ConnectedProductContainer.propTypes = {
  location: PropTypes.object,
  match: PropTypes.object.isRequired,
}

export default withApiError(
  ConnectedProductContainer,
  [ERROR_TYPE_PROFILE, ERROR_TYPE_LMS],
  [SHOW_ERROR_IN_MODAL],
)
